/**
 * Mist autocomplete, snippets, hover, and formatting providers for Monaco.
 *
 * All keywords, snippets, and hover docs are derived from the actual
 * rivellum-mist-parser lexer/parser and the mistc CLI.
 */
import type * as Monaco from 'monaco-editor';
import { MIST_LANGUAGE_ID } from './mistLanguage';

/* ================================================================== */
/*  Keyword & built-in documentation                                  */
/* ================================================================== */

interface MistKeywordDoc {
  label: string;
  detail: string;
  documentation: string;
  kind: 'keyword' | 'type' | 'intrinsic' | 'valueOp';
}

const KEYWORD_DOCS: MistKeywordDoc[] = [
  // Structure
  { label: 'contract', detail: 'Contract declaration', documentation: 'Declares a new Mist smart contract. A file must contain exactly one contract.\n\nExample:\n```\ncontract MyToken\n    state supply: Int\n```', kind: 'keyword' },
  { label: 'state', detail: 'State variable', documentation: 'Declares a persistent state variable in the contract.\n\nSyntax: `state name: Type`\n\nTypes: Int, Bool, String, Address, Timestamp, Value, U128, U64, Asset, VaultRef', kind: 'keyword' },
  { label: 'function', detail: 'Function declaration', documentation: 'Declares a contract function.\n\nSyntax:\n```\nfunction name(param: Type) -> ReturnType\n    body\n```', kind: 'keyword' },
  { label: 'event', detail: 'Event declaration', documentation: 'Declares an event that can be emitted during execution.\n\nSyntax: `event Transfer(from, to, amount)`', kind: 'keyword' },
  { label: 'policy', detail: 'Value policy', documentation: 'Declares a value policy block that controls minting, burning, and transfer rules.', kind: 'keyword' },

  // Control flow
  { label: 'if', detail: 'Conditional branch', documentation: 'Conditional execution.\n\nSyntax:\n```\nif condition\n    body\nelse\n    body\n```', kind: 'keyword' },
  { label: 'else', detail: 'Else branch', documentation: 'Alternative branch for an `if` statement.', kind: 'keyword' },
  { label: 'for', detail: 'Bounded for loop', documentation: 'Deterministic bounded loop.\n\nAllowed syntax:\n```\nfor i in 0..10\n    body\n```\nor\n```\nfor i in 0..list_len(players)\n    body\n```\nLoop bound must be provable at compile time.', kind: 'keyword' },
  { label: 'while', detail: 'Forbidden loop form', documentation: 'While loops are forbidden in Mist deterministic subset.\n\nUse bounded for loops instead.', kind: 'keyword' },
  { label: 'return', detail: 'Return value', documentation: 'Returns a value from a function.\n\nSyntax: `return expression`', kind: 'keyword' },
  { label: 'let', detail: 'Variable binding', documentation: 'Declares a local variable.\n\nSyntax: `let name = expression` or `let name: Type = expression`', kind: 'keyword' },

  // Assertions
  { label: 'require', detail: 'Require condition', documentation: 'Asserts a condition, reverting if false.\n\nSyntax: `require condition` or `require condition, "error message"`', kind: 'keyword' },
  { label: 'assert', detail: 'Assert condition', documentation: 'Asserts an invariant condition.\n\nSyntax: `assert condition` or `assert condition, "error message"`', kind: 'keyword' },
  { label: 'emit', detail: 'Emit event', documentation: 'Emits a declared event.\n\nSyntax: `emit EventName(arg1, arg2)`', kind: 'keyword' },

  // Mint/Burn policy
  { label: 'can_mint', detail: 'Mint authority policy', documentation: 'Declares that the contract can mint new tokens under specified conditions.', kind: 'keyword' },
  { label: 'can_burn', detail: 'Burn authority policy', documentation: 'Declares that the contract can burn tokens under specified conditions.', kind: 'keyword' },

  // Value operations
  { label: 'hold', detail: 'Hold value in vault', documentation: 'Locks value into a vault from a source.\n\nSyntax: `hold amount asset "ASSET" from source`\nor: `hold v = amount asset "ASSET" from source` (named vault)', kind: 'valueOp' },
  { label: 'pay', detail: 'Pay value to address', documentation: 'Sends value to a destination address.\n\nSyntax: `pay amount asset "ASSET" to destination`\nor: `pay amount asset "ASSET" to destination from vault_ref`', kind: 'valueOp' },
  { label: 'release', detail: 'Release vault to address', documentation: 'Releases all remaining value in a vault to an address.\n\nSyntax: `release vault v to destination`', kind: 'valueOp' },
  { label: 'refund', detail: 'Refund vault to address', documentation: 'Refunds vault contents back to the original depositor.\n\nSyntax: `refund vault v to destination`', kind: 'valueOp' },
  { label: 'split', detail: 'Split vault into portions', documentation: 'Splits vault value into multiple destinations.\n\nSyntax:\n```\nsplit vault v into [\n    50% to addr1\n    50% to addr2\n]\n```\n\nPercentages must sum to exactly 100%.', kind: 'valueOp' },
  { label: 'mint', detail: 'Mint new tokens', documentation: 'Creates new tokens under a mint authority.\n\nSyntax: `mint amount asset "ASSET" authority auth_ref`\nor: `mint v = amount asset "ASSET" authority auth_ref` (named)', kind: 'valueOp' },
  { label: 'burn', detail: 'Burn tokens from vault', documentation: 'Permanently destroys tokens held in a vault.\n\nSyntax: `burn vault v`', kind: 'valueOp' },

  // Intrinsics
  { label: 'now', detail: 'Current timestamp', documentation: 'Returns the current block timestamp as a Timestamp value.', kind: 'intrinsic' },
  { label: 'caller', detail: 'Transaction sender', documentation: 'Returns the Address of the account that initiated the current transaction.', kind: 'intrinsic' },
  { label: 'intent', detail: 'Current intent', documentation: 'Returns the current intent context.', kind: 'intrinsic' },
  { label: 'oracle', detail: 'Oracle data access', documentation: 'Reads external oracle data.\n\nSyntax: `oracle.price("ASSET_PAIR")`', kind: 'intrinsic' },

  // Types
  { label: 'Int', detail: 'Integer type', documentation: 'Signed 64-bit integer type.', kind: 'type' },
  { label: 'Bool', detail: 'Boolean type', documentation: 'Boolean type: true or false.', kind: 'type' },
  { label: 'String', detail: 'String type', documentation: 'UTF-8 string type.', kind: 'type' },
  { label: 'Address', detail: 'Address type', documentation: '32-byte account/contract address, derived as BLAKE3(public_key)[0..32].', kind: 'type' },
  { label: 'Timestamp', detail: 'Timestamp type', documentation: 'Unix timestamp (seconds since epoch).', kind: 'type' },
  { label: 'Value', detail: 'Value type', documentation: 'Represents a monetary value amount.', kind: 'type' },
  { label: 'U128', detail: '128-bit unsigned integer', documentation: 'Unsigned 128-bit integer type.', kind: 'type' },
  { label: 'U64', detail: '64-bit unsigned integer', documentation: 'Unsigned 64-bit integer type.', kind: 'type' },
  { label: 'Asset', detail: 'Asset type', documentation: 'Represents a token/asset identifier.', kind: 'type' },
  { label: 'VaultRef', detail: 'Vault reference type', documentation: 'Reference to a value vault created by hold/mint operations.', kind: 'type' },
  { label: 'List', detail: 'Bounded list type', documentation: 'List<T, CAP> bounded collection. CAP is mandatory and must be > 0.', kind: 'type' },
  { label: 'Set', detail: 'Bounded set type', documentation: 'Set<T, CAP> bounded membership collection. No iteration in v1.', kind: 'type' },
  { label: 'Map', detail: 'Bounded map type', documentation: 'Map<K, V, CAP> bounded key-value collection. No iteration in v1.', kind: 'type' },
];

/* ================================================================== */
/*  Snippets                                                          */
/* ================================================================== */

interface MistSnippet {
  label: string;
  detail: string;
  documentation: string;
  insertText: string;
}

const SNIPPETS: MistSnippet[] = [
  {
    label: 'contract',
    detail: 'Contract skeleton',
    documentation: 'Creates a new contract with state, init function, and a starter function.',
    insertText: [
      'contract ${1:MyContract}',
      '    state owner: Address',
      '',
      '    function init()',
      '        owner = caller',
      '        emit Initialized(owner)',
      '',
      '    function ${2:my_function}(${3:param}: ${4:Int})',
      '        require caller == owner',
      '        $0',
    ].join('\n'),
  },
  {
    label: 'state',
    detail: 'State variable declaration',
    documentation: 'Declares a persistent state variable.',
    insertText: 'state ${1:name}: ${2|Int,Bool,String,Address,Timestamp,Value,U128,U64,Asset,VaultRef|}',
  },
  {
    label: 'function',
    detail: 'Function block',
    documentation: 'Creates a new function with parameters and body.',
    insertText: [
      'function ${1:name}(${2:param}: ${3:Int})',
      '    ${0}',
    ].join('\n'),
  },
  {
    label: 'function-return',
    detail: 'Function with return type',
    documentation: 'Creates a function that returns a value.',
    insertText: [
      'function ${1:name}(${2:param}: ${3:Int}) -> ${4:Int}',
      '    return ${0}',
    ].join('\n'),
  },
  {
    label: 'event',
    detail: 'Event declaration',
    documentation: 'Declares an event with parameters.',
    insertText: 'event ${1:EventName}(${2:param1}, ${3:param2})',
  },
  {
    label: 'if',
    detail: 'If block',
    documentation: 'Conditional if block.',
    insertText: [
      'if ${1:condition}',
      '    ${0}',
    ].join('\n'),
  },
  {
    label: 'if-else',
    detail: 'If/else block',
    documentation: 'Conditional if/else block.',
    insertText: [
      'if ${1:condition}',
      '    ${2}',
      'else',
      '    ${0}',
    ].join('\n'),
  },
  {
    label: 'require',
    detail: 'Require assertion',
    documentation: 'Assert a condition, reverting if false.',
    insertText: 'require ${1:condition}',
  },
  {
    label: 'require-msg',
    detail: 'Require with message',
    documentation: 'Assert a condition with an error message.',
    insertText: 'require ${1:condition}, "${2:error message}"',
  },
  {
    label: 'assert',
    detail: 'Assert statement',
    documentation: 'Assert an invariant condition.',
    insertText: 'assert ${1:condition}, "${2:message}"',
  },
  {
    label: 'emit',
    detail: 'Emit event',
    documentation: 'Emit a declared event.',
    insertText: 'emit ${1:EventName}(${2:args})',
  },
  {
    label: 'let',
    detail: 'Variable declaration',
    documentation: 'Declare a local variable.',
    insertText: 'let ${1:name} = ${0}',
  },
  {
    label: 'let-typed',
    detail: 'Typed variable declaration',
    documentation: 'Declare a local variable with explicit type.',
    insertText: 'let ${1:name}: ${2|Int,Bool,String,Address|} = ${0}',
  },
  {
    label: 'hold',
    detail: 'Hold value in vault',
    documentation: 'Lock value into a vault.',
    insertText: 'hold ${1:v} = ${2:amount} asset ${3:"RVL"} from ${4:sender}',
  },
  {
    label: 'pay',
    detail: 'Pay value to address',
    documentation: 'Send value to a destination.',
    insertText: 'pay ${1:amount} asset ${2:"RVL"} to ${3:recipient}',
  },
  {
    label: 'release',
    detail: 'Release vault',
    documentation: 'Release vault contents to an address.',
    insertText: 'release vault ${1:v} to ${2:recipient}',
  },
  {
    label: 'refund',
    detail: 'Refund vault',
    documentation: 'Refund vault contents back to depositor.',
    insertText: 'refund vault ${1:v} to ${2:sender}',
  },
  {
    label: 'split',
    detail: 'Split vault',
    documentation: 'Split vault into percentage-based portions. Percentages must sum to 100%.',
    insertText: [
      'split vault ${1:v} into [',
      '    ${2:50}% to ${3:addr1}',
      '    ${4:50}% to ${5:addr2}',
      ']',
    ].join('\n'),
  },
  {
    label: 'mint',
    detail: 'Mint tokens',
    documentation: 'Create new tokens under mint authority.',
    insertText: 'mint ${1:v} = ${2:amount} asset ${3:"RVL"} authority ${4:owner}',
  },
  {
    label: 'burn',
    detail: 'Burn tokens from vault',
    documentation: 'Destroy tokens held in a vault.',
    insertText: 'burn vault ${1:v}',
  },
  {
    label: 'policy',
    detail: 'Value policy block',
    documentation: 'Declare token minting and burning policy.',
    insertText: [
      'policy',
      '    can_mint',
      '        require caller == owner',
      '    can_burn',
      '        require caller == owner',
    ].join('\n'),
  },
  {
    label: 'escrow-pattern',
    detail: 'Escrow pattern (hold + release/refund)',
    documentation: 'Common escrow pattern: hold funds, then release or refund based on condition.',
    insertText: [
      '# Escrow pattern',
      'hold escrow_vault = ${1:amount} asset ${2:"RVL"} from ${3:buyer}',
      'if ${4:condition}',
      '    release vault escrow_vault to ${5:seller}',
      'else',
      '    refund vault escrow_vault to ${3:buyer}',
    ].join('\n'),
  },
  {
    label: 'owner-guard',
    detail: 'Owner-only function guard',
    documentation: 'Common pattern: restrict function to contract owner.',
    insertText: 'require caller == owner, "Only the contract owner can call this function"',
  },
];

/* ================================================================== */
/*  Register providers                                                */
/* ================================================================== */

export function registerMistProviders(monaco: typeof Monaco): void {
  // ----- Completion Provider -----
  monaco.languages.registerCompletionItemProvider(MIST_LANGUAGE_ID, {
    triggerCharacters: ['.', ' '],

    provideCompletionItems(model, position) {
      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      };

      const lineContent = model.getLineContent(position.lineNumber);
      const textBefore = lineContent.substring(0, position.column - 1).trim();

      // After 'oracle.', suggest 'price'
      if (textBefore.endsWith('oracle.')) {
        return {
          suggestions: [{
            label: 'price',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'price("${1:ASSET_PAIR}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            detail: 'Read oracle price',
            documentation: 'Reads the current price from an oracle feed.\n\nSyntax: oracle.price("ETH/USD")',
          }],
        };
      }

      // After ':' suggest types
      if (textBefore.endsWith(':')) {
        const typeItems = KEYWORD_DOCS
          .filter(d => d.kind === 'type')
          .map(d => ({
            label: d.label,
            kind: monaco.languages.CompletionItemKind.TypeParameter,
            insertText: ' ' + d.label,
            range,
            detail: d.detail,
            documentation: d.documentation,
            sortText: '0' + d.label,
          }));
        return { suggestions: typeItems };
      }

      const suggestions: Monaco.languages.CompletionItem[] = [];

      // Snippets
      for (const snip of SNIPPETS) {
        suggestions.push({
          label: snip.label,
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: snip.insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: snip.detail,
          documentation: snip.documentation,
          sortText: '1' + snip.label,
        });
      }

      // Keywords
      for (const doc of KEYWORD_DOCS) {
        let kind: Monaco.languages.CompletionItemKind;
        switch (doc.kind) {
          case 'keyword': kind = monaco.languages.CompletionItemKind.Keyword; break;
          case 'type': kind = monaco.languages.CompletionItemKind.TypeParameter; break;
          case 'intrinsic': kind = monaco.languages.CompletionItemKind.Function; break;
          case 'valueOp': kind = monaco.languages.CompletionItemKind.Method; break;
        }
        suggestions.push({
          label: doc.label,
          kind,
          insertText: doc.label,
          range,
          detail: doc.detail,
          documentation: doc.documentation,
          sortText: '2' + doc.label,
        });
      }

      return { suggestions };
    },
  });

  // ----- Hover Provider -----
  monaco.languages.registerHoverProvider(MIST_LANGUAGE_ID, {
    provideHover(model, position) {
      const word = model.getWordAtPosition(position);
      if (!word) return null;

      const doc = KEYWORD_DOCS.find(d => d.label === word.word);
      if (!doc) return null;

      const kindLabel = doc.kind === 'valueOp' ? 'value operation'
        : doc.kind === 'intrinsic' ? 'intrinsic'
        : doc.kind === 'type' ? 'type'
        : 'keyword';

      return {
        range: new monaco.Range(
          position.lineNumber, word.startColumn,
          position.lineNumber, word.endColumn,
        ),
        contents: [
          { value: `**${doc.label}** *(${kindLabel})*` },
          { value: doc.documentation },
        ],
      };
    },
  });

  // ----- Document Formatting Provider -----
  monaco.languages.registerDocumentFormattingEditProvider(MIST_LANGUAGE_ID, {
    provideDocumentFormattingEdits(model) {
      return formatMistSource(model);
    },
  });

  // ----- Document Range Formatting Provider -----
  monaco.languages.registerDocumentRangeFormattingEditProvider(MIST_LANGUAGE_ID, {
    provideDocumentRangeFormattingEdits(model, range) {
      return formatMistSource(model, range);
    },
  });
}

/* ================================================================== */
/*  Mist Formatter                                                    */
/* ================================================================== */

/**
 * Safe Mist formatter that normalises indentation without changing semantics.
 * Based on the real Mist indentation rules from rivellum-mist-parser.
 *
 * Rules:
 * - 4-space indentation (matching the Mist compiler convention)
 * - Indent after block starters: contract, function, if, else, for, while, policy
 * - Dedent at else (matching level of its if)
 * - Tabs are converted to spaces
 * - Trailing whitespace is removed
 * - Blank lines are preserved but trimmed
 */
function formatMistSource(
  model: Monaco.editor.ITextModel,
  range?: Monaco.IRange,
): Monaco.languages.TextEdit[] {
  const startLine = range?.startLineNumber ?? 1;
  const endLine = range?.endLineNumber ?? model.getLineCount();

  const lines: string[] = [];
  for (let i = startLine; i <= endLine; i++) {
    lines.push(model.getLineContent(i));
  }

  const BLOCK_STARTERS = /^\s*(contract|function|if|else|for|while|policy)\b/;
  const INDENT_WIDTH = 4;
  const formatted: string[] = [];
  let indentLevel = 0;

  // For range formatting, detect the starting indent level
  if (range && startLine > 1) {
    const prevLine = model.getLineContent(startLine - 1);
    const prevStripped = prevLine.trimStart();
    const prevIndent = prevLine.length - prevStripped.length;
    indentLevel = Math.floor(prevIndent / INDENT_WIDTH);
    if (BLOCK_STARTERS.test(prevLine)) {
      indentLevel++;
    }
  }

  // First pass: determine indent levels
  const levels: number[] = [];
  let level = indentLevel;
  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].trim();

    if (stripped.length === 0 || stripped.startsWith('#')) {
      levels.push(stripped.startsWith('#') ? level : -1); // -1 = blank line
      continue;
    }

    // Dedent for `else` to match `if`
    if (/^else\b/.test(stripped)) {
      level = Math.max(0, level - 1);
    }

    levels.push(level);

    // Indent after block starters
    if (BLOCK_STARTERS.test(stripped)) {
      level++;
    }
  }

  // Second pass: format
  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].replace(/\t/g, '    ').trim();

    if (stripped.length === 0) {
      formatted.push('');
      continue;
    }

    const lvl = levels[i] === -1 ? 0 : levels[i];
    formatted.push(' '.repeat(lvl * INDENT_WIDTH) + stripped);
  }

  const fullRange: Monaco.IRange = {
    startLineNumber: startLine,
    startColumn: 1,
    endLineNumber: endLine,
    endColumn: model.getLineMaxColumn(endLine),
  };

  return [{ range: fullRange, text: formatted.join('\n') }];
}
